import { createFormConfig, ValidationRules } from 'helpers/createFormConfig';
import * as Yup from 'yup';

// ============================================================================
// Field Names
// ============================================================================

export const SpecialConditionsFormFieldNames = {
  // Main fields
  BankAccountId: 'bankAccountId',
  FrequencyCode: 'frequencyCode',
  StartDate: 'startDate',
  // Outstanding amounts
  ExistingOutstanding: 'existingOutstanding',
  ExistingOutstandingInsurer: 'existingOutstandingInsurer',
  // Bank info
  CurrentLoanNumber: 'currentLoanNumber',
  // Bank contact
  BankEntityName: 'bankContact.entityName',
  BankEntityAddressSearch: 'bankContact.entityAddressSearch',
  BankEntityAdditionalAddress: 'bankContact.entityAddress.additionalAddress',
  BankLastName: 'bankContact.lastName',
  BankFirstName: 'bankContact.firstName',
  BankPhone: 'bankContact.phone',
  BankEmail: 'bankContact.email',
  // Insurance contact (RENEG_COURTIER only)
  InsuranceEntityName: 'insuranceContact.entityName',
  InsuranceEntityAddressSearch: 'insuranceContact.entityAddressSearch',
  InsuranceEntityAdditionalAddress: 'insuranceContact.entityAddress.additionalAddress',
  InsuranceLastName: 'insuranceContact.lastName',
  InsuranceFirstName: 'insuranceContact.firstName',
  InsurancePhone: 'insuranceContact.phone',
  InsuranceEmail: 'insuranceContact.email'
} as const;

export type SpecialConditionsFormFieldNames =
  (typeof SpecialConditionsFormFieldNames)[keyof typeof SpecialConditionsFormFieldNames];

// ============================================================================
// Types
// ============================================================================

export interface AddressFormValues {
  address?: string;
  additionalAddress?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  googlePlaceId?: string;
  latitude?: number;
  longitude?: number;
}

export interface ContactFormValues {
  entityName?: string;
  entityAddressSearch?: string;
  entityAddress?: AddressFormValues;
  lastName?: string;
  firstName?: string;
  phone?: string;
  email?: string;
}

export interface SpecialConditionsFormValues {
  bankAccountId: string;
  frequencyCode: string;
  startDate: string;
  existingOutstanding: number;
  existingOutstandingInsurer: number;
  currentLoanNumber?: string;
  bankContact: ContactFormValues;
  insuranceContact?: ContactFormValues;
}

// ============================================================================
// Schema
// ============================================================================

const addressSchema = Yup.object().shape({
  address: Yup.string().nullable(),
  additionalAddress: Yup.string()
    .nullable()
    .max(100, "Le complément d'adresse ne doit pas dépasser 100 caractères"),
  postalCode: Yup.string().nullable(),
  city: Yup.string().nullable(),
  country: Yup.string().nullable(),
  googlePlaceId: Yup.string().nullable(),
  latitude: Yup.number().nullable(),
  longitude: Yup.number().nullable()
});

export const specialConditionsValidationRules: ValidationRules<
  keyof SpecialConditionsFormValues
> = {
  bankAccountId: Yup.string().required('Le compte bancaire est requis'),
  frequencyCode: Yup.string().required('La fréquence est requise'),
  startDate: Yup.string()
    .required('La date de début est requise')
    .test('date-range', "La date d'effet doit être comprise entre aujourd'hui et 6 mois", (value) => {
      if (!value) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const maxDate = new Date();
      maxDate.setMonth(maxDate.getMonth() + 6);
      maxDate.setHours(23, 59, 59, 999);
      const date = new Date(value);
      return date >= today && date <= maxDate;
    }),
  existingOutstanding: Yup.number()
    .required("L'encours existant est requis")
    .min(0, 'Le montant doit être positif'),
  existingOutstandingInsurer: Yup.number()
    .required("L'encours assureur existant est requis")
    .min(0, 'Le montant doit être positif'),
  currentLoanNumber: Yup.string().nullable(),
  bankContact: Yup.object().nullable(),
  insuranceContact: Yup.object().nullable()
};

// ============================================================================
// Dynamic Contact Schemas (based on selected insurance project code)
// ============================================================================

const entityAddressSearchSchema = Yup.string()
  .required("L'adresse de l'établissement est requise")
  .test(
    'google-place-selected',
    'BORROWER_INSURED_FORM.VALIDATION.ADDRESS_SELECTION_REQUIRED',
    function () {
      const { entityAddress } = this.parent;
      return !!(entityAddress && entityAddress.city);
    }
  );

const contactRequiredSchema = Yup.object().shape({
  entityName: Yup.string().required("Le nom de l'établissement est requis").max(200),
  entityAddressSearch: entityAddressSearchSchema,
  entityAddress: addressSchema.nullable(),
  lastName: Yup.string().max(100).nullable(),
  firstName: Yup.string().max(100).nullable(),
  phone: Yup.string().max(20).nullable(),
  email: Yup.string().email('Email invalide').max(200).nullable()
}).required();

const contactOptionalSchema = Yup.object().shape({
  entityName: Yup.string().required("Le nom de l'établissement est requis").max(200),
  entityAddressSearch: entityAddressSearchSchema,
  entityAddress: addressSchema.nullable(),
  lastName: Yup.string().max(100).nullable(),
  firstName: Yup.string().max(100).nullable(),
  phone: Yup.string().max(20).nullable(),
  email: Yup.string().email('Email invalide').max(200).nullable()
});

export const getDynamicSchemaFields = (insuranceProjectCode: string | null) => ({
  bankContact: contactRequiredSchema,
  insuranceContact: insuranceProjectCode === 'RENEG_COURTIER'
    ? contactOptionalSchema
    : Yup.object().nullable()
});

// ============================================================================
// Helpers
// ============================================================================

export const calculateDueDate = (startDate: string): Date | null => {
  if (!startDate) return null;
  const date = new Date(startDate);
  date.setFullYear(date.getFullYear() + 1);
  return date;
};

// ============================================================================
// Form Config
// ============================================================================

export const specialConditionsFormConfig =
  createFormConfig<SpecialConditionsFormValues>(
    specialConditionsValidationRules as ValidationRules<
      keyof SpecialConditionsFormValues
    >
  );
